/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectCurrentUserApiToken } from "../slices/currentUser/currentUserSlice";
import { selectAllResources } from "../slices/resourceCollection/resourceCollectionSlice";
import { selectEditMode } from "../slices/editMode/editModeSlice";
import { selectOlderVersionFhirString, selectViewingOlderVersion } from "../slices/viewingOlderVersion/viewingOlderVersionSlice";
import { generateCitationSummaryFromFhirJson } from "../functions/generateHowToCite";
import axiosInstance from '../functions/axiosInstance';
import MetadataPatternEdit from "./MetadataOfResourceEditing";
import SimpleResourceFieldViewer from "./SimpleResourceFieldViewer";
import { MetadataPatternDisplay } from "./MetadataPatternDisplay";
import NewTabLink from "../components/NewTabLink";
import { DisplayFromFHIRMarkdown } from "../datatypeComponents/MarkdownEntry";
import server_config from '../ServerConfiguration';
import { displayStringFromFHIRHumanName } from "../datatypeComponents/HumanNameEntry";
import ContainedResourceDisplay from "./resourceDisplayCommonComponents/ContainedResourceDisplay";

const fevir_node_server_address = server_config["fevir_node_server_address"];

const getPublicReferencedResources = async (targetfoi, apiToken = null) => {
    return await axiosInstance({
        method: 'POST',
        url: fevir_node_server_address,
        timeout: 10000,
        data: {
            'functionid': 'getpublicreferenced',
            'apiToken': apiToken,
            'targetfoi': targetfoi
        },
    }).then((resp) => {
        if (resp?.data) {
            return resp.data.references;
        }
        console.log("NO RESPONSE from geterrors call");
    }).catch((error) => { console.log(error); });
}

const ResourceLinkList = ({ resources, currentFOI, idKey = "id", resourceTypeKey = "resourceType", titleKey = "displayTitle" }) => {
    return <div style={{ marginLeft: "24px" }}>
        {resources.map((entry, index) => {
            const url = "/resources/" + entry[resourceTypeKey] + "/" + entry[idKey];
            return <div key={index}>
                {entry[idKey] === currentFOI ?
                    <></>
                    :
                    <NewTabLink href={url} target={'foi-' + entry[idKey]}>
                        <b>{entry[resourceTypeKey]}: </b>{entry[titleKey]} (FOI {entry[idKey]})
                    </NewTabLink>
                }
            </div>
        })}
    </div>
}

const AssociatedResources = ({ fhirJson, resourceCollection, viewingOlderVersion }) => {
    const apiToken = useSelector(selectCurrentUserApiToken);

    const [associatedLoading, setAssociatedLoading] = useState(true);
    const [whereReferenced, setWhereReferenced] = useState([]);
    const [whereReferencedExpanded, setWhereReferencedExpanded] = useState(false);
    const [expandContainedResourcesState, setExpandContainedResourcesState] = useState(false);

    const loadReferences = async () => {
        //setAssociatedLoading(true);
        const referencedResources = (await getPublicReferencedResources(fhirJson.id, apiToken)) || [];
        if (referencedResources.length > 0) {
            setWhereReferenced(referencedResources);
            if (referencedResources.length <= 5) {
                setWhereReferencedExpanded(true); //Auto opens up the Referenced Resources if 5 or less, otherwise it will stay collapsed
            }
        }
        setAssociatedLoading(false);
    }

    useEffect(() => {
        loadReferences();
    }, []);

    return <>
        <h3 id="associated-resources">Associated Resources</h3>
        {fhirJson.contained?.length > 0 &&
            <div style={{ marginLeft: "24px" }}>
                <h4>Contained Resources <span className={"unselectable"}
                    style={{ cursor: "pointer", color: "blue" }}
                    onClick={() => { setExpandContainedResourcesState(!expandContainedResourcesState); }}
                >{expandContainedResourcesState ? " ▼ collapse resource detail" : " ► expand resource detail"}
                </span></h4>
                <div style={{ marginLeft: "24px" }}>
                    {fhirJson.contained.map((resource, resourceIndex) => {
                        let resourceType = resource.resourceType;
                        let titleDisplay = "Untitled Resource";
                        if (resource.title) {
                            titleDisplay = resource.title;
                        } else if (resource.name) {
                            if (resourceType === "Practitioner") {
                                if (resource.name[0]) {
                                    titleDisplay = displayStringFromFHIRHumanName(resource.name[0]);
                                }
                            } else {
                                titleDisplay = resource.name;
                            }
                        } else if (resource.display) {
                            titleDisplay = resource.display;
                        }
                        return <div key={resourceIndex}>
                            <p><b>{resourceType}:</b> {titleDisplay}</p>
                            {expandContainedResourcesState === true &&
                                <div style={{ marginLeft: "24px" }}>
                                    <ContainedResourceDisplay resource={resource} />
                                </div>}
                        </div>
                    })}
                </div>
            </div>
        }
        {viewingOlderVersion && <h4>NOTE: These are the Resources associated with the CURRENT version, not the older version you are viewing.</h4>}
        <div style={{ marginLeft: "24px" }}>
            {resourceCollection?.length > 1 ? <>
                <div style={{ textAlign: "left", marginTop: "8px", marginBottom: "8px" }}>
                    <span style={{fontSize: "1.07142857rem", marginBottom: "0px !important"}}>Resources referenced by <b>{fhirJson.resourceType}: {fhirJson.title || fhirJson.name || "this Resource"}</b> (FOI {fhirJson.id}) include:</span>
                    <ResourceLinkList resources={resourceCollection} currentFOI={fhirJson.id} idKey="id" resourceTypeKey="resourceType" titleKey="displayTitle" />
                </div>
            </>
                :
                <p>No referenced Resources.</p>}
        </div>
        {associatedLoading ?
            <img style={{ height: "22px" }} src="/spinner.gif" alt="Loading" />
            :
            <div style={{ marginLeft: "24px" }}>
                {whereReferenced.length ?
                    <>
                        <h4
                            style={{ cursor: "pointer", marginBottom: "0px" }}
                            onClick={() => { setWhereReferencedExpanded(prevState => { return !prevState; }); }}
                        >Where Publicly Referenced ({whereReferenced.length}) <span className="unselectable">{whereReferencedExpanded ? "▼" : "▶"}</span></h4>
                        {whereReferencedExpanded && <ResourceLinkList resources={whereReferenced} currentFOI={fhirJson.id} idKey="sourcefoi" resourceTypeKey="sourceresourcetype" titleKey="sourcetitle" />}
                    </>
                    :
                    <span style={{ fontSize: "14px" }}>No public Resources reference this one.</span>
                }
            </div>}
    </>
}

const ResourceBottomDisplay = ({ fhirJson, setJsonViewEditingState, setStatusChanged }) => {
    const editMode = useSelector(state => selectEditMode(state));
    const resourceCollection = useSelector(state => selectAllResources(state));
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const olderVersionFhirString = useSelector(state => selectOlderVersionFhirString(state));

    let fhirJsonToUse = fhirJson;
    if (viewingOlderVersion && olderVersionFhirString) {
        try {
            const olderVersionJson = JSON.parse(olderVersionFhirString);
            fhirJsonToUse = olderVersionJson;
        } catch {
            alert("Error viewing JSON of older version.");
        }
    }

    if (!fhirJsonToUse) {
        return <></>
    }

    let resourceType = fhirJsonToUse.resourceType;

    return <div>
        {resourceType !== "Citation" && <>
            <h3 id="how-to-cite">How to Cite</h3>
            <div style={{ marginLeft: "24px" }}>
                <DisplayFromFHIRMarkdown markdown={generateCitationSummaryFromFhirJson(fhirJsonToUse)} />
            </div>
        </>}
        {resourceType === "Citation"
            ?
            <h3 id="citation-metadata">Citation Metadata</h3>
            :
            <h3 id="metadata">Metadata</h3>
        }
        <div style={{ marginLeft: "24px" }}>
            {editMode ?
                <MetadataPatternEdit
                    foi={fhirJson.id}
                    setJsonViewEditingState={setJsonViewEditingState}
                    setStatusChanged={setStatusChanged}
                />
                :
                <MetadataPatternDisplay fhirJson={fhirJsonToUse} />
            }
        </div>
        <AssociatedResources fhirJson={fhirJson} resourceCollection={resourceCollection} viewingOlderVersion={viewingOlderVersion} />
        <h3 id="json-outline">JSON Outline</h3>
        <SimpleResourceFieldViewer resource={fhirJsonToUse} parentElement={""} />
        <br />
        <br />
    </div>
}

export default ResourceBottomDisplay;